"use client";

import { useDeferredValue, useMemo, useState } from "react";

import Link from "next/link";

import { Search, X } from "lucide-react";

import { buildCanonicalBrandPath } from "@/lib/brand-routing";
import type { BrandListEntry } from "@/lib/public-shop-agent-fetch";

import BrandLetterNav from "./BrandLetterNav";
import {
  getHeadingId,
  getLetterAnchorId,
  groupBrandsByLetter,
  normalizeBrandSearchMatchText,
  normalizeBrandSearchText,
} from "./brands-directory-utils";

interface BrandsDirectoryClientProps {
  brands: BrandListEntry[];
}

function getBrandCountLabel(count: number): string {
  return `${count} ${count === 1 ? "brand" : "brands"}`;
}

export default function BrandsDirectoryClient({ brands }: BrandsDirectoryClientProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const deferredSearchTerm = useDeferredValue(searchTerm);

  const isInputActive = searchTerm.trim().length > 0;
  const trimmedSearchTerm = deferredSearchTerm.trim();
  const isSearchActive = trimmedSearchTerm.length > 0;

  const filteredBrands = useMemo(() => {
    const normalizedQuery = normalizeBrandSearchText(deferredSearchTerm);
    const normalizedMatchQuery = normalizeBrandSearchMatchText(deferredSearchTerm);
    if (!normalizedQuery) {
      return brands;
    }

    return brands.filter((brand) => {
      const normalizedBrandName = normalizeBrandSearchText(brand.brand_name);
      if (normalizedBrandName.includes(normalizedQuery)) {
        return true;
      }

      if (!normalizedMatchQuery) {
        return false;
      }

      return normalizeBrandSearchMatchText(brand.brand_name).includes(normalizedMatchQuery);
    });
  }, [brands, deferredSearchTerm]);

  const groups = useMemo(() => groupBrandsByLetter(filteredBrands), [filteredBrands]);
  const letterNavItems = useMemo(
    () =>
      groups.map(({ letter }) => ({
        letter,
        targetId: getLetterAnchorId(letter),
      })),
    [groups]
  );

  return (
    <>
      <div className="mb-8 pb-6 md:mb-10">
        <label
          htmlFor="brands-directory-search"
          className="mb-3 block text-overline tracking-widest text-ink-muted"
        >
          Search Brands
        </label>
        <div className="relative max-w-2xl">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted"
          />
          <input
            id="brands-directory-search"
            type="text"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search designers or brands"
            aria-label="Search brands"
            className="w-full rounded-full border border-divider bg-white py-3 pl-11 pr-24 text-body text-ink outline-none transition-colors placeholder:text-ink-faint focus:border-ink"
          />
          {isInputActive ? (
            <button
              type="button"
              onClick={() => setSearchTerm("")}
              className="absolute right-3 top-1/2 inline-flex -translate-y-1/2 items-center gap-1 rounded-full px-2 py-1 text-[14px] leading-none text-ink-secondary transition-colors hover:text-ink"
              aria-label="Clear brand search"
            >
              <X className="h-3 w-3" />
              <span>Clear</span>
            </button>
          ) : null}
        </div>
        {isSearchActive ? (
          <p className="mt-3 text-caption text-ink-secondary">
            {`Showing ${getBrandCountLabel(filteredBrands.length)} for "${trimmedSearchTerm}".`}
          </p>
        ) : null}
      </div>

      {!isSearchActive && groups.length > 0 ? <BrandLetterNav items={letterNavItems} /> : null}

      {groups.length === 0 ? (
        isSearchActive ? (
          <p className="text-body text-ink-secondary">No brands match "{trimmedSearchTerm}".</p>
        ) : null
      ) : (
        <div className="space-y-10">
          {groups.map(({ letter, brands: groupBrands }) => (
            <section
              key={letter}
              id={getLetterAnchorId(letter)}
              aria-labelledby={getHeadingId(letter)}
              className="scroll-mt-28 md:scroll-mt-32"
            >
              <h2 id={getHeadingId(letter)} className="text-title font-semibold text-ink">
                {letter}
              </h2>
              <ul className="mt-4 grid gap-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                {groupBrands.map((brand, index) => (
                  <li key={`${brand.brand_name}-${index}`}>
                    <Link
                      href={buildCanonicalBrandPath({
                        brandName: brand.brand_name,
                        brandSlug: brand.brand_slug,
                      })}
                      prefetch={false}
                      className="text-body text-ink-secondary transition-colors hover:text-ink hover:underline"
                    >
                      {brand.brand_name}
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </>
  );
}
